/**
 * When a file dropped or pasted anywhere in the staff portal opens "Create case", and when it is left
 * alone (Lloyd, Oct 2026: "create a client / case by simply dragging a ticket to any page").
 *
 * The page-wide drop must never take a file meant for somewhere else. The places that already take
 * files keep them:
 *  - any drop zone that handles its own drop: it calls preventDefault, and React runs its handler
 *    before the event reaches the window, so the event arrives here already claimed;
 *  - anything marked `data-own-drop` (Upload Ticket, the calendar, Bulk Notices, the Files tabs);
 *  - a file input itself.
 * It also stands aside while any dialog is open, so nothing lands behind one.
 *
 * Paste only acts on an image (a screenshot, a captured part of a page), and only when the cursor is
 * not in somewhere that takes pasted content — a message box or the letter editor keeps its paste.
 */

/** The bits of an Element these rules read, so the tests need no DOM. */
export interface DropTargetLike {
  closest(selector: string): unknown;
}

export const OWN_DROP_SELECTOR = '[data-own-drop], input[type="file"]';

export function isFileDrag(types: ReadonlyArray<string> | DOMStringList | null | undefined): boolean {
  if (!types) return false;
  return Array.from(types as ArrayLike<string>).includes('Files');
}

export function shouldHandleGlobalDrop(
  event: {
    defaultPrevented: boolean;
    types: ReadonlyArray<string> | DOMStringList | null | undefined;
    target: DropTargetLike | null;
  },
  context: { dialogOpen: boolean; otherModalOpen: boolean },
): boolean {
  if (!isFileDrag(event.types)) return false;
  if (event.defaultPrevented) return false;
  if (context.dialogOpen || context.otherModalOpen) return false;
  if (event.target && typeof event.target.closest === 'function' && event.target.closest(OWN_DROP_SELECTOR)) {
    return false;
  }
  return true;
}

/** True for a focused element that takes pasted content itself. */
export function isEditableTarget(
  element: { tagName?: string; isContentEditable?: boolean } | null | undefined,
): boolean {
  if (!element) return false;
  if (element.isContentEditable) return true;
  const tag = (element.tagName ?? '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** The pasted image to read, or null. Text pastes are never taken. */
export function pastedImage(
  files: ReadonlyArray<{ type: string }>,
  context: { focused: { tagName?: string; isContentEditable?: boolean } | null; dialogOpen: boolean; otherModalOpen: boolean },
): number | null {
  if (context.otherModalOpen) return null;
  if (!context.dialogOpen && isEditableTarget(context.focused)) return null;
  const index = files.findIndex((file) => file.type.startsWith('image/'));
  return index >= 0 ? index : null;
}
